import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import axios from 'axios';
import '../App.css';

interface PublicInviteInfo {
  team: { id: string; name: string; teamNumber: string } | null;
  position?: string;
  jerseyNumber?: number;
  creator: { firstName: string; lastName: string } | null;
  matchDates: Array<{ id: string; scheduledDate: string; scheduledTime?: string }>;
  guestFee?: number;
}

interface FullInviteInfo {
  id: string;
  teamId: string;
  token: string;
  position?: string;
  jerseyNumber?: number;
  expiresAt: string;
  team: { id: string; name: string; teamNumber: string } | null;
  creator: { id: string; firstName: string; lastName: string } | null;
  matches: Array<{
    id: string;
    scheduledDate: string;
    scheduledTime?: string;
    status: string;
    homeTeam?: { id: string; name: string };
    awayTeam?: { id: string; name: string };
  }>;
  isAlreadyGuest: boolean;
  eligibility: {
    allowed: boolean;
    currentCount: number;
    limit: number | null;
    remaining: number | null;
  };
  guestFee?: number;
}

const AcceptGuestInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [publicInfo, setPublicInfo] = useState<PublicInviteInfo | null>(null);
  const [fullInfo, setFullInfo] = useState<FullInviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [acceptedPaymentId, setAcceptedPaymentId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchFullDetails();
    } else {
      fetchPublicInfo();
    }
  }, [token, user]);

  const fetchPublicInfo = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await axios.get(`${baseUrl}/guest-invites/${token}/public`);
      setPublicInfo(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load invite');
    } finally {
      setLoading(false);
    }
  };

  const fetchFullDetails = async () => {
    try {
      const response = await api.get(`/guest-invites/${token}`);
      setFullInfo(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load invite');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!fullInfo) return;
    setAccepting(true);
    try {
      const response = await api.post(`/guest-invites/${token}/accept`);
      if (response.data.paymentId) {
        setAcceptedPaymentId(response.data.paymentId);
      } else {
        alert(`Successfully joined ${response.data.team.name} as a guest player!`);
        navigate(`/teams/${response.data.team.id}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <p>Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <h2>Invite Error</h2>
          <div className="error">{error}</div>
          {!user && (
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <a href={`/login?redirect=/guest-invite/${token}`} className="btn btn-primary">Log In</a>
              <a href={`/register?redirect=/guest-invite/${token}`} className="btn btn-secondary">Register</a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Not logged in - show public landing page
  if (!user && publicInfo) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <h1>Guest Player Invite</h1>
          <div style={{ marginTop: '20px' }}>
            {publicInfo.team && (
              <p><strong>Team:</strong> {publicInfo.team.name} ({publicInfo.team.teamNumber})</p>
            )}
            {publicInfo.creator && (
              <p><strong>Invited by:</strong> {publicInfo.creator.firstName} {publicInfo.creator.lastName}</p>
            )}
            {publicInfo.position && (
              <p><strong>Position:</strong> {publicInfo.position}</p>
            )}
            {publicInfo.matchDates.length > 0 && (
              <div style={{ marginTop: '15px' }}>
                <strong>Match Dates:</strong>
                <ul style={{ marginTop: '5px' }}>
                  {publicInfo.matchDates.map((m) => (
                    <li key={m.id}>
                      {new Date(m.scheduledDate).toLocaleDateString()}
                      {m.scheduledTime && ` at ${m.scheduledTime}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {publicInfo.guestFee && (
              <p style={{ marginTop: '10px' }}><strong>Guest Fee:</strong> ${publicInfo.guestFee.toFixed(2)}</p>
            )}
          </div>

          <div style={{ marginTop: '30px', padding: '15px', background: '#FFF3E0', borderRadius: '8px' }}>
            <p style={{ margin: 0 }}>
              <strong>Log in or create an account</strong> to accept this guest player invite.
            </p>
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <a href={`/login?redirect=/guest-invite/${token}`} className="btn btn-primary">Log In</a>
            <a href={`/register?redirect=/guest-invite/${token}`} className="btn btn-secondary">Register</a>
          </div>
        </div>
      </div>
    );
  }

  // Logged in but no invite info
  if (!fullInfo) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <h2>Invite Not Found</h2>
          <p>The invite link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  // Already a guest
  if (fullInfo.isAlreadyGuest) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <h2>Already a Guest</h2>
          <p>You are already a guest player on {fullInfo.team?.name}.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/teams/${fullInfo.teamId}`)}>
            View Team
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
      <div className="card">
        <h1>Guest Player Invite</h1>
        <div style={{ marginTop: '20px' }}>
          <p><strong>Team:</strong> {fullInfo.team?.name} ({fullInfo.team?.teamNumber})</p>
          {fullInfo.creator && (
            <p><strong>Invited by:</strong> {fullInfo.creator.firstName} {fullInfo.creator.lastName}</p>
          )}
          {fullInfo.position && (
            <p><strong>Position:</strong> {fullInfo.position}</p>
          )}
          {fullInfo.jerseyNumber && (
            <p><strong>Jersey Number:</strong> #{fullInfo.jerseyNumber}</p>
          )}
          <p><strong>Expires:</strong> {new Date(fullInfo.expiresAt).toLocaleDateString()}</p>
          {fullInfo.guestFee && (
            <p><strong>Guest Fee:</strong> ${fullInfo.guestFee.toFixed(2)}</p>
          )}
        </div>

        {acceptedPaymentId && (
          <div style={{ marginTop: '20px', padding: '15px', background: '#E8F5E9', borderRadius: '8px' }}>
            <p style={{ margin: '0 0 10px 0' }}>
              <strong>You've joined the team!</strong> A guest fee of ${fullInfo.guestFee?.toFixed(2)} is due.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/payments')}>
              Pay Now
            </button>
            <button className="btn btn-secondary" style={{ marginLeft: '10px' }} onClick={() => navigate(`/teams/${fullInfo.teamId}`)}>
              Pay Later
            </button>
          </div>
        )}

        {fullInfo.matches.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h3>Matches</h3>
            <table className="table" style={{ fontSize: '14px' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Matchup</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {fullInfo.matches.map((m) => (
                  <tr key={m.id}>
                    <td>{new Date(m.scheduledDate).toLocaleDateString()}</td>
                    <td>{m.homeTeam?.name || 'TBD'} vs {m.awayTeam?.name || 'TBD'}</td>
                    <td>
                      <span className={`badge ${m.status === 'scheduled' ? 'badge-info' : m.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                        {m.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {fullInfo.eligibility.limit !== null && (
          <div style={{ marginTop: '15px', padding: '15px', background: '#E8F5E9', borderRadius: '8px' }}>
            <p style={{ margin: 0 }}>
              <strong>Guest Games:</strong> {fullInfo.eligibility.currentCount} played
              {fullInfo.eligibility.remaining !== null && ` / ${fullInfo.eligibility.remaining} remaining`}
              {fullInfo.eligibility.limit !== null && ` (limit: ${fullInfo.eligibility.limit})`}
            </p>
          </div>
        )}

        {!fullInfo.eligibility.allowed && fullInfo.eligibility.limit !== null ? (
          <div style={{ marginTop: '20px', padding: '15px', background: '#FFEBEE', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#C62828' }}>
              <strong>Not Eligible:</strong> You have reached the guest player game limit for this league.
            </p>
          </div>
        ) : (
          <div style={{ marginTop: '30px', padding: '15px', background: '#E8F5E9', borderRadius: '8px' }}>
            <p style={{ margin: 0 }}>
              <strong>Welcome, {user?.firstName}!</strong> Click below to join as a guest player.
            </p>
          </div>
        )}

        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-primary"
            onClick={handleAccept}
            disabled={accepting || (!fullInfo.eligibility.allowed && fullInfo.eligibility.limit !== null)}
          >
            {accepting ? 'Joining...' : 'Accept & Join'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptGuestInvite;
